/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "xero",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Xero",
          "depth": 1,
          "id": "connect-to-xero"
        }, {
          "value": "Connect to your data source",
          "depth": 1,
          "id": "connect-to-your-data-source"
        }, {
          "value": "Create a model",
          "depth": 1,
          "id": "create-a-model"
        }, {
          "value": "Create a sync",
          "depth": 1,
          "id": "create-a-sync"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Object selection",
          "depth": 1,
          "id": "object-selection"
        }, {
          "value": "Accounts",
          "depth": 1,
          "id": "accounts"
        }, {
          "value": "Contacts",
          "depth": 1,
          "id": "contacts"
        }, {
          "value": "Payments",
          "depth": 1,
          "id": "payments"
        }, {
          "value": "Invoices",
          "depth": 1,
          "id": "invoices"
        }, {
          "value": "Items",
          "depth": 1,
          "id": "items"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      strong: "strong",
      h4: "h4",
      ol: "ol",
      li: "li",
      code: "code",
      ul: "ul",
      a: "a",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Give yourself confidence in the financial health of your business by integrating your Xero usage with Hightouch.\nWe automate your financial workflows and eliminate inconsistent data issues and manual data entry."
      }), "\n", _jsx(_components.p, {
        children: "With this integration, you can deliver up-to-date data directly from your source of truth to allow your team the luxury to focus\non making financial decisions that benefit your business instead of wasting time finding, cleaning, and moving relevant data."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-xero",
        children: "Connect to Xero"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Authorize Xero with OAuth and create your destination."
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-xero-as-a-destination-in-hightouch",
        children: "Add Xero as a destination in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Hightouch or create a free Hightouch account"
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Destinations"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add destination"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Xero"
          }), " in the destination catalog and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsx(_components.li, {
          children: "Complete the authorization steps until you are redirected back to the Hightouch application"
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, ", _jsx(_components.code, {
            children: "Xero Production"
          }), " and ", _jsx(_components.code, {
            children: "Xero-production"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your Xero destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-data-source",
        children: "Connect to your data source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sits on top of your data warehouse and can read from Snowflake, Redshift, BigQuery, Databricks, Postgres, MySQL, and many other data sources. You can even fetch data from Google Sheets and Airtable. Hightouch can help activate your data wherever it resides."
      }), "\n", _jsx(_components.p, {
        children: "We've written dedicated guides for each supported data source. Take your pick:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/airtable",
            children: "Airtable"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/s3",
            children: "Amazon S3"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/clickhouse",
            children: "ClickHouse"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/elasticsearch",
            children: "Elasticsearch"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/firebolt",
            children: "Firebolt"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-sheets",
            children: "Google Sheets"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/metabase",
            children: "Metabase"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/mongodb",
            children: "MongoDB"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/mysql",
            children: "MySQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/rockset",
            children: "Rockset"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sftp",
            children: "SFTP"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sqlserver",
            children: "SQL Server"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If you're missing the credentials necessary to connect to your data source,\ntry inviting your teammates to Hightouch. You can have unlimited collaborators\nin your workspace."
        })
      }), "\n", _jsx(_components.h4, {
        id: "connect-to-a-sample-database-instead",
        children: "Connect to a sample database instead"
      }), "\n", _jsxs(_components.p, {
        children: ["Waiting for credentials to your data warehouse? No problem. You can connect to one of our ", _jsx(_components.a, {
          href: "/sources/sample-data",
          children: "sample datasets"
        }), " to get started right away. For the rest of this tutorial, we'll use the ", _jsx(_components.strong, {
          children: "B2B SaaS"
        }), " sample source, which contains a ", _jsx(_components.code, {
          children: "users"
        }), " table. This sample source is available when you log in for the first time—no setup required."]
      }), "\n", _jsx(_components.p, {
        children: "Next, we'll write a SQL query to define which users should be synced to our Xero accounts resource. You can follow the same pattern to sync to other Xero resources."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-model",
        children: "Create a model"
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, a model represents a query that filters or transforms the records in your data source. Models are used to determine exactly which records to sync between sources and destinations."
      }), "\n", _jsx(_components.p, {
        children: "When you connect to a data warehouse, your models will probably be written in SQL. You can also import models from tools like dbt and Looker."
      }), "\n", _jsx(_components.p, {
        children: "In this example, we'll keep it simple. Suppose you want to sync all your customers to Xero. To accomplish this, you'll want to create a model that looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users;\n"]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Models"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add model"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Demo Database"
          }), " as your source and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SQL Editor"
          }), " as your modeling method."]
        }), "\n", _jsx(_components.li, {
          children: "Paste the SQL query from above into the editor on the left side of the page."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Preview"
          }), " to execute the query."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a name and unique slug (for example, \"All Users\" and ", _jsx(_components.code, {
            children: "all-users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "ID"
          }), " as the primary key for your model."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your model."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "We just created a model using a SQL query that retrieves all our current users. Next, we'll configure our sync to add these users to Xero."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync",
        children: "Create a sync"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Syncs"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add sync"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "All Users"
          }), " as your model and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Xero"
          }), " as your destination and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "object-selection",
        children: "Object selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Xero resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Accounts"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contacts"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Payments"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Invoices"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Items"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "accounts",
        children: "Accounts"
      }), "\n", _jsx(_components.h4, {
        id: "account-type-selection",
        children: "Account type selection"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Select the type of accounts you would like to sync to your destination. To sync dynamic types, toggle 'use column' and select a column from your model that contains supported enum values by Xero."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Selecting an account type from the drop-down menu allows Hightouch to provide\nyou with the appropriate validations for each account type. These validations\nwill not exist if you choose to sync dynamic types from your model and will\nrequire you to be familiar with what is required by Xero."
        })
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["For ", _jsx(_components.strong, {
          children: "Bank"
        }), " account type, our integration supports:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " mode."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For all other account types, our integration supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": Records that aren't found in Xero given the provided identifier will be inserted. Existing records will be updated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": Records that are found in Xero given the provided identifier will be updated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Records will be pushed to your destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Xero workspace by:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Account ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Name"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Xero fields."
      }), "\n", _jsx(_components.h4, {
        id: "delete-records",
        children: "Delete records"
      }), "\n", _jsx(_components.p, {
        children: "When the Hightouch sync engine recognizes that a record has been removed from your source, you can either:"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Do nothing. An existing record in the destination would remain untouched."
        }), "\n", _jsx(_components.li, {
          children: "Delete the record in the destination. Only non-system accounts and accounts not used on transactions can be deleted."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "contacts",
        children: "Contacts"
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes-1",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " mode: Records that aren't found in Xero given the provided identifier will be inserted. Existing records will be updated."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Xero workspace by:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Xero fields."
      }), "\n", _jsx(_components.h3, {
        id: "payments",
        children: "Payments"
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes-2",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " mode: Records will be pushed to your destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "invoice-record-matching",
        children: "Invoice record matching"
      }), "\n", _jsx(_components.p, {
        children: "Invoice records can be matched from your source to your Xero workspace by:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Invoice ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Invoice Number"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Credit Note ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Credit Note Number"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "account-record-matching",
        children: "Account record matching"
      }), "\n", _jsx(_components.p, {
        children: "Account records can be matched from your source to your Xero workspace by:"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Account ID"
        }), "\n", _jsx(_components.strong, {
          children: "Code"
        })]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Xero fields."
      }), "\n", _jsx(_components.h3, {
        id: "invoices",
        children: "Invoices"
      }), "\n", _jsx(_components.h4, {
        id: "invoice-type-selection",
        children: "Invoice type selection"
      }), "\n", _jsx(_components.p, {
        children: "Select the type of invoices you would like to sync to your destination. To sync dynamic types, toggle 'use column' and select a column from your model that contains supported enum values by Xero."
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes-3",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " mode: Records that aren't found in Xero given the provided identifier will be inserted. Existing records will be updated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " mode: Records that aren't found in Xero given the provided identifier will be inserted."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, records can be matched from your source to your Xero workspace by:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Invoice Number"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-3",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Xero fields."
      }), "\n", _jsx(_components.h4, {
        id: "associations",
        children: "Associations"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can perform a reference look-up for reference fields. Select an identifier to use to perform the look-up and use the respective contact to create the invoice object."
      }), "\n", _jsx(_components.p, {
        children: "You can perform a look-up using:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Contact Number"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email Address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Name"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If the ", _jsx(_components.code, {
          children: "Contact ID"
        }), " exists in your model, you can skip the lookup step by selecting ", _jsx(_components.code, {
          children: "Contact ID"
        }), " from the drop-down menu."]
      }), "\n", _jsx(_components.h3, {
        id: "items",
        children: "Items"
      }), "\n", _jsx(_components.h4, {
        id: "invoice-type-selection-1",
        children: "Invoice type selection"
      }), "\n", _jsx(_components.p, {
        children: "Select the type of invoices you would like to sync to your destination. To sync dynamic types, toggle 'use column' and select a column from your model that contains supported enum values by Xero."
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes-4",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " mode: Records that aren't found in Xero given the provided identifier will be inserted. Existing records will be updated."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), " mode: Records will be pushed to your destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-3",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, records can be matched from your source to your Xero workspace by:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Item Code"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-4",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported Xero fields."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
